import type {
  BankerAumPayload,
  BankerCaseDetailPayload,
  ClientDetailPayload,
  ClientsListPayload,
  HomeNbaPayload,
  ProductCatalogPayload,
  ProductDetailPayload,
} from '../contracts'
import { apiFetch } from '../auth/apiClient'

function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  const next = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.replace(`/login?next=${next}`)
}

async function handleErrorResponse(response: Response): Promise<never> {
  if (response.status === 401) redirectToLogin()
  let message = `Request failed with status ${response.status}`
  try {
    const payload = (await response.json()) as { error?: string }
    if (payload?.error) message = payload.error
  } catch {
    // Fall back to the generic status message.
  }
  throw new Error(message)
}

async function fetchJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await apiFetch(url, {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

async function postJson<T>(
  url: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

export function fetchProducts(search: string, signal: AbortSignal): Promise<ProductCatalogPayload> {
  return fetchJson<ProductCatalogPayload>(`/api/explorer/products${search}`, signal)
}

export function fetchProduct(search: string, signal: AbortSignal): Promise<ProductDetailPayload> {
  return fetchJson<ProductDetailPayload>(`/api/explorer/product${search}`, signal)
}

export function fetchClients(search: string, signal: AbortSignal): Promise<ClientsListPayload> {
  return fetchJson<ClientsListPayload>(`/api/clients${search}`, signal)
}

export function fetchClientDetail(
  clientCode: string,
  search: string,
  signal: AbortSignal,
): Promise<ClientDetailPayload> {
  return fetchJson<ClientDetailPayload>(
    `/api/clients/${encodeURIComponent(clientCode)}${search}`,
    signal,
  )
}

export function createClientProposalCase(
  clientCode: string,
  search: string,
): Promise<{ case: BankerCaseDetailPayload; created: boolean }> {
  return postJson(`/api/clients/${encodeURIComponent(clientCode)}/proposal-case${search}`, {})
}

export function fetchBankerAum(signal: AbortSignal): Promise<BankerAumPayload> {
  return fetchJson<BankerAumPayload>('/api/banker/me/aum', signal)
}

/**
 * Homepage NBA — three-card grid generated by the home_nba service.
 *
 * Failure is soft: callers should render the quiet empty-state line rather
 * than surfacing an error toast (this is a dashboard widget, not a
 * transactional surface). See HomeRoute.
 *
 * PROTOTYPE: see `services/home_nba/README.md` for the parameters engineers
 * will need to tune per client and the in-memory state-store caveat.
 */
export function fetchHomeNba(signal: AbortSignal): Promise<HomeNbaPayload> {
  return fetchJson<HomeNbaPayload>('/api/banker/home-nba', signal)
}

export function acceptBankerSignal(
  signalId: string,
  advisorCode: string,
): Promise<{ case: BankerCaseDetailPayload }> {
  return postJson(`/api/banker/signals/${encodeURIComponent(signalId)}/accept`, {
    advisor_code: advisorCode,
    current_flow: 'evidence_review',
  })
}
